import { useRef } from 'react';
import {
    Box,
    Text,
    HStack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Badge,
} from '@chakra-ui/react';
import Heading from '../../../../components/v4/Heading';
import Button from '../../../../components/v4/Button';
import { ArrowBackIcon, CheckCircleIcon } from '@chakra-ui/icons';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
export default function APIResultsView({
    error,
    succeed,
    handleDone,
    succeedAPIColumns,
    failedAPIColumns,
}) {
    const gridRef = useRef();
    const { data: currentCompanyData } = useGetCurrentCompany();
    const companyName = currentCompanyData?.name ?? 'your company';

    const succeedData = succeed.map((res) => res.data);
    const hasSucceed = succeedData.length > 0;
    const hasFailed = error.length > 0;
    const hasAnyResult = hasSucceed || hasFailed;
    const noChanges = !hasAnyResult;

    const sectionHeaderStyles = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        py: 2,
    };

    const CountBadge = ({ count, colorScheme }) => (
        <Badge
            colorScheme={colorScheme}
            borderRadius="full"
            px={2}
            fontSize="xs"
            ml={2}>
            {count}
        </Badge>
    );

    return (
        <Box display="block">
            {/* Centered status header */}
            <Box
                display="flex"
                direction="column"
                alignItems="center"
                py={8}
                mb={6}
                flexDirection="column">
                <CheckCircleIcon
                    boxSize={16}
                    color={noChanges ? 'blue.400' : 'green.400'}
                    mb={4}
                />
                <Heading size="xl" textAlign="center" mb={2}>
                    {noChanges ? 'No changes detected' : 'Import successful'}
                </Heading>
                <Text color="gray.500" fontSize="sm" textAlign="center">
                    {noChanges
                        ? `No differences found for ${companyName}.`
                        : `Processed for ${companyName}.`}
                </Text>
            </Box>

            {hasAnyResult && (
                <Accordion
                    allowMultiple
                    defaultIndex={hasSucceed ? [0] : [1]}
                    mb={4}>
                    {/* MODIFIED RECORDS (API succeed) */}
                    <AccordionItem
                        mb={2}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        overflow="hidden">
                        <AccordionButton
                            bg="gray.50"
                            _hover={{ bg: 'gray.100' }}
                            _expanded={{ bg: 'green.50' }}
                            _focus={{ boxShadow: 'none' }}>
                            <Box sx={sectionHeaderStyles}>
                                <Box display="flex" alignItems="center">
                                    <Text
                                        fontWeight="bold"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        color="green.700">
                                        Modified Records
                                    </Text>
                                    <CountBadge
                                        count={succeedData.length}
                                        colorScheme="green"
                                    />
                                </Box>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <AccordionIcon color="green.600" />
                                </Box>
                            </Box>
                        </AccordionButton>
                        <AccordionPanel pb={0} px={0}>
                            {hasSucceed ? (
                                <Box
                                    style={{
                                        maxHeight: '350px',
                                        overflowY: 'auto',
                                    }}>
                                    <AGGridMain
                                        gridRef={gridRef}
                                        isLoading={false}
                                        refetch={null}
                                        overlayNoRowsTemplate="No results"
                                        tableData={{
                                            columns: succeedAPIColumns,
                                            data: succeedData,
                                        }}
                                        suppressMenuHide={false}
                                        getQuickFilterText={null}
                                        columnDefs={succeedAPIColumns}
                                    />
                                </Box>
                            ) : (
                                <Box px={4} py={3}>
                                    <Text fontSize="sm" color="gray.500">
                                        No records were successfully modified.
                                    </Text>
                                </Box>
                            )}
                        </AccordionPanel>
                    </AccordionItem>

                    {/* FAILED RECORDS (API errors) */}
                    <AccordionItem
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        overflow="hidden">
                        <AccordionButton
                            bg="gray.50"
                            _hover={{ bg: 'gray.100' }}
                            _expanded={{ bg: 'red.50' }}
                            _focus={{ boxShadow: 'none' }}>
                            <Box sx={sectionHeaderStyles}>
                                <Box display="flex" alignItems="center">
                                    <Text
                                        fontWeight="bold"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        color="red.700">
                                        Failed Records
                                    </Text>
                                    <CountBadge
                                        count={error.length}
                                        colorScheme="red"
                                    />
                                </Box>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <AccordionIcon color="red.600" />
                                </Box>
                            </Box>
                        </AccordionButton>
                        <AccordionPanel pb={0} px={0}>
                            {hasFailed ? (
                                <Box
                                    style={{
                                        maxHeight: '350px',
                                        overflowY: 'auto',
                                    }}>
                                    <AGGridMain
                                        gridRef={gridRef}
                                        isLoading={false}
                                        refetch={null}
                                        overlayNoRowsTemplate="No failed records"
                                        tableData={{
                                            columns: failedAPIColumns,
                                            data: error,
                                        }}
                                        suppressMenuHide={false}
                                        getQuickFilterText={null}
                                        columnDefs={failedAPIColumns}
                                    />
                                </Box>
                            ) : (
                                <Box px={4} py={3}>
                                    <Text fontSize="sm" color="gray.500">
                                        No failed records.
                                    </Text>
                                </Box>
                            )}
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}

            {/* Bottom actions */}
            <HStack spacing={3} mt={4}>
                <Button
                    leftIcon={<ArrowBackIcon />}
                    variant="outline"
                    onClick={handleDone}>
                    Upload another file
                </Button>
            </HStack>
        </Box>
    );
}
